<template>
  <cv-overflow-menu-item class="overflow-menu-item" :class="cssClasses" @click="handleClick">
    <slot />
  </cv-overflow-menu-item>
</template>

<script setup lang="ts">
import { useAttrs } from "vue";
import { computed } from "vue";

const attrs = useAttrs();
const emit = defineEmits(["click"]);
const handleClick = (event: Event) => {
  if (attrs.disabled) {
    return;
  }
  emit("click", event);
};

interface Props {
  textNormal?: boolean;
  disabled?: boolean;
}

const props = defineProps<Props>();

//[TODO:FE] - temp handling for inconsistent local, spring cleaning to be done on R5S1, to check out ALL locale usage in overflow menu item
const cssClasses = computed(() => ({
  "text-normal": props.textNormal,
  disabled: props.disabled,
}));
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/typo";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.cv-overflow-menu-item {
  &.bx--overflow-menu-options__option {
    min-height: 2.5rem;
    height: auto;

    &:first-of-type {
      border-radius: 10px 10px 0 0;
    }

    &:last-of-type {
      border-radius: 0 0 10px 10px;
    }

    &:only-of-type {
      border-radius: 10px;

      .bx--overflow-menu-options__btn {
        padding: $spacing-03 $spacing-05; // to balance the spacing if only 1 option
      }
    }

    &._has-tooltip {
      opacity: 1;
      &.disabled {
        .bx--overflow-menu-options__btn {
          opacity: 1;

          &:has(.edit-option-content) {
            .title {
              color: $disabled-01;
            }
            .label {
              @include carbon--type-style("card-short-01");
              color: $disabled-01;
            }
            .bx--tooltip__trigger {
              align-items: flex-start;
            }
          }
          .label {
            @include carbon--type-style("body-short-01");
            opacity: 0.5;
            display: inline-flex;
            align-items: center;
          }

          .bx--tooltip__trigger {
            display: flex;
            width: 100%;
            cursor: not-allowed;
          }
        }
      }

      .bx--overflow-menu-options__option-content {
        width: 100%;
        overflow: visible;
      }
    }

    &.bx--overflow-menu-options__option--disabled {
      opacity: 0.5;

      button[disabled] {
        // Fix multiply opacities
        opacity: 1;
      }

      .bx--overflow-menu-options__option-content {
        .sls-icon {
          opacity: 0.5;
        }
      }

      // OVerride background color as original will cause flickering when hover
      &:hover {
        background-color: transparent;
      }
    }

    &.disabled {
      &:hover {
        background-color: $ui-01;
        cursor: not-allowed;
      }
      &:active,
      &:focus {
        pointer-events: none;
      }

      .bx--overflow-menu-options__btn {
        color: $disabled-01;
        opacity: 0.5;
        cursor: not-allowed;

        &:hover {
          color: $disabled-02;
        }
      }
    }

    &.header {
      .bx--overflow-menu-options__option-content {
        @include carbon--type-style("label-03", true);
        color: $text-03;
      }

      &:hover {
        background-color: $ui-01;

        .bx--overflow-menu-options__btn {
          cursor: default;
        }
      }
    }

    &.optional {
      border-top: 1px solid $ui-02;
    }

    &.overflow-menu-item-header {
      min-height: auto;

      &:hover {
        background-color: transparent;
      }

      .bx--overflow-menu-options__btn {
        @include carbon--type-style("label-03", true);
        text-transform: uppercase;
        color: $text-03;
        pointer-events: none;

        .bx--overflow-menu-options__option-content {
          text-transform: uppercase;
        }
      }
    }

    &.text-normal {
      .bx--overflow-menu-options__option-content {
        text-transform: initial;
      }
    }

    .bx--overflow-menu-options__option-content {
      display: flex;
      align-items: center;
      white-space: normal; // opt to break to next line if text is too long
      text-transform: capitalize;

      &:has(.edit-option-content) {
        align-items: flex-start;
        text-transform: none;
      }

      > .icon-wrapper {
        width: 24px;
        margin-right: $spacing-03;
      }

      .toolbar-option {
        display: flex;
        align-items: center;
      }

      .sls-icon {
        flex: 0 0 auto;
        margin-right: $spacing-03;
        fill: transparent;
        min-width: 24px;

        &[name*="UpvoteUnfilled24"] {
          > path {
            fill: $icon-02;
          }
        }

        &[name*="StarMinus24"] {
          [stroke] {
            stroke: $icon-02;
          }
          [fill] {
            fill: $icon-02;
          }
        }
      }
    }

    .bx--overflow-menu-options__btn {
      max-width: 100%;
      padding: $spacing-03;

      &:hover {
        color: $text-02;
      }

      &:focus {
        outline: none;
      }

      &:focus-visible {
        outline: 3px solid $inverse-support-04;
      }

      .input-checkbox {
        margin: 0 (-$spacing-03);
        padding: 0 $spacing-03;

        > label {
          align-items: center;
        }
      }

      .bx--tooltip__trigger {
        text-align: left;
      }
    }
  }
}

.edit-overflow-item {
  .title {
    @include carbon--type-style("card-title-01");
    color: $text-01;
    margin-bottom: $spacing-02;
  }
  .label {
    @include carbon--type-style("card-short-01");
    color: $text-02;
  }
}
</style>
